import React from "react";
import { HardHat, House, Buildings, ArrowRight } from "@phosphor-icons/react";

const CATEGORIES = [
  {
    id: "contractors",
    label: "Contractors",
    eyebrow: "Renos · Restorations · Roofers",
    icon: HardHat,
    title: "Keep the site clean. Keep the crew moving.",
    body:
      "From new builds and renovation tear-outs to restoration jobs and roof tear-offs — Troya Mini Bins delivers the right bin on the right day. Same-day or next-day delivery, fast swap-outs, and honest weighing so your project stays on schedule and on budget.",
    bullets: [
      "Construction debris & demo",
      "Renovation cleanouts",
      "Restoration jobs",
      "Roofing tear-offs",
    ],
    ctaHref: "#pricing",
    ctaLabel: "Get a quote",
  },
  {
    id: "residential",
    label: "Residential",
    eyebrow: "Homeowners · DIY · Cleanouts",
    icon: House,
    title: "Renovation, cleanout, or DIY? We&apos;ll help.",
    body:
      "Planning a kitchen reno, basement cleanout, garage purge, or long-overdue spring cleanup? Call your bin friends — we&apos;ll match you to the right size, place it carefully on your driveway, and haul it away when you&apos;re done.",
    bullets: [
      "Kitchen, bath & basement renos",
      "Garage & estate cleanouts",
      "Spring & seasonal cleanups",
    ],
    ctaHref: "#pricing",
    ctaLabel: "Get a quote",
  },
  {
    id: "commercial",
    label: "Commercial",
    eyebrow: "Apartment Buildings · Property Managers · Retail · Non-Profits",
    icon: Buildings,
    title: "We handle the waste. You handle business.",
    body:
      "From apartment buildings and multi-site property managers to retail plazas, non-profit organizations and other commercial operations — we accommodate any scale. Recurring drop offs, predictable terms, and fast callbacks when you reach out.",
    bullets: [
      "Apartment Buildings / Multi-Family",
      "Property Management Companies (multiple sites)",
      "Retail Plazas / Stores",
      "Non-Profit Organizations",
      "Recurring commercial service",
    ],
    ctaHref: "#commercial-pricing",
    ctaLabel: "Request a custom quote",
  },
];

export default function WhoWeServe() {
  return (
    <section
      data-testid="who-we-serve-section"
      id="who-we-serve"
      className="bg-white border-b-2 border-slate-200"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-8 py-20 lg:py-28">
        <div className="grid lg:grid-cols-12 gap-10 mb-14 items-end">
          <div className="lg:col-span-7">
            <div className="text-[11px] font-extrabold tracking-[0.22em] uppercase text-sky-600">
              Who we serve
            </div>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-slate-900 mt-3 leading-tight">
              One Toronto bin company.
              <br className="hidden sm:block" /> Every type of job.
            </h2>
          </div>
          <p className="lg:col-span-5 text-slate-600 leading-relaxed">
            Troya Mini Bins serves contractors, Toronto homeowners and commercial clients with the same
            care and the same fair pricing. Find your category below &mdash; or just call, and we&apos;ll
            sort it out together.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-0 border-2 border-slate-900 divide-y-2 md:divide-y-0 md:divide-x-2 divide-slate-900">
          {CATEGORIES.map((c, idx) => (
            <article
              key={c.id}
              data-testid={`audience-card-${c.id}`}
              className={`group relative bg-white p-7 sm:p-9 hover:bg-slate-50 transition-colors flex flex-col ${
                idx === 1 ? "lg:bg-slate-50" : ""
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="inline-flex items-center justify-center h-14 w-14 border-2 border-slate-900 bg-white group-hover:bg-sky-500 group-hover:border-sky-500 transition-colors">
                  <c.icon
                    size={30}
                    weight="bold"
                    className="text-slate-900 group-hover:text-white transition-colors"
                  />
                </div>
                <span className="font-display font-black text-5xl text-slate-100 group-hover:text-sky-100 transition-colors leading-none">
                  0{idx + 1}
                </span>
              </div>

              {/* Category headline — BIG and dominant */}
              <h3
                data-testid={`audience-label-${c.id}`}
                className="font-display font-black text-4xl sm:text-5xl lg:text-[2.75rem] xl:text-5xl text-slate-900 mt-7 leading-[0.95] tracking-tight uppercase"
              >
                {c.label}
              </h3>
              <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-sky-600 mt-3">
                {c.eyebrow}
              </div>

              {/* Subhead — smaller, supporting role */}
              <p
                className="font-display font-semibold text-base text-slate-700 mt-5 leading-snug"
                dangerouslySetInnerHTML={{ __html: c.title }}
              />
              <p
                className="mt-3 text-slate-600 leading-relaxed text-[14px] flex-1"
                dangerouslySetInnerHTML={{ __html: c.body }}
              />

              <ul className="mt-5 space-y-2">
                {c.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm text-slate-700">
                    <span className="mt-2 h-1.5 w-1.5 bg-sky-500 shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>

              <a
                href={c.ctaHref}
                data-testid={`audience-cta-${c.id}`}
                className="mt-7 inline-flex items-center gap-2 font-bold text-xs uppercase tracking-[0.18em] text-slate-900 group-hover:text-sky-600 transition-colors border-b-2 border-slate-900 group-hover:border-sky-500 pb-1 w-fit"
              >
                {c.ctaLabel}
                <ArrowRight size={14} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
              </a>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
